/**
 * Dashboard Settings Component
 *
 * Slide-out drawer with user preferences:
 * - Top N selector (5/10/15/20)
 * - Language toggle (AR/EN)
 * - Auto-refresh interval (uses incremental refresh ~5 seconds)
 * - Full refresh schedule (configurable day/time)
 * - Reset to defaults button
 */

import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { X, RotateCcw, Settings as SettingsIcon, Clock, RefreshCw, Calendar } from 'lucide-react';
import { useSettings } from '../hooks/useSettings';
import { useTranslation } from '../hooks/useTranslation';
import { appConfig } from '../config/AppConfig';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 1000;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
`;

const Drawer = styled.div`
  position: fixed;
  top: 0;
  ${props => props.isRTL ? 'left: 0' : 'right: 0'};
  height: 100vh;
  width: 340px;
  background: white;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.15);
  z-index: 1001;
  transform: translateX(${props => {
    if (props.isOpen) return '0';
    return props.isRTL ? '-100%' : '100%';
  }});
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e2e8f0;
`;

const Title = styled.h2`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1e293b;
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: #f1f5f9;
  border-radius: 8px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.2s;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
  }
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 24px;
`;

const Section = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #64748b;
  margin: 0 0 16px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SectionDescription = styled.p`
  font-size: 12px;
  color: #94a3b8;
  margin: -8px 0 12px 0;
  line-height: 1.4;
`;

const OptionGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const OptionButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? '#3b82f6' : '#e2e8f0'};
  background: ${props => props.active ? '#eff6ff' : 'white'};
  color: ${props => props.active ? '#1d4ed8' : '#64748b'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    background: ${props => props.active ? '#dbeafe' : '#f8fafc'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const LanguageToggle = styled.div`
  display: flex;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 4px;
  width: 100%;
`;

const LanguageOption = styled.button`
  flex: 1;
  padding: 12px 16px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.active ? 'white' : 'transparent'};
  color: ${props => props.active ? '#1e293b' : '#64748b'};
  box-shadow: ${props => props.active ? '0 2px 8px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    color: #1e293b;
  }
`;

const Footer = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #e2e8f0;
`;

const ResetButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e1;
    color: #1e293b;
  }
`;

const TOP_N_OPTIONS = [5, 10, 15, 20];

// Auto-refresh interval presets (in minutes)
// Now uses incremental refresh which is fast (~5 seconds)
const REFRESH_PRESETS = [
  { value: 0, labelAr: 'إيقاف', labelEn: 'Off' },
  { value: 60, labelAr: '1 ساعة', labelEn: '1 hour' },
  { value: 360, labelAr: '6 ساعات', labelEn: '6 hours' },
  { value: 720, labelAr: '12 ساعة', labelEn: '12 hours' },
  { value: 1440, labelAr: '24 ساعة', labelEn: '24 hours' },
];

// Minimum interval in minutes (1 hour)
const MIN_INTERVAL_MINUTES = 60;

// Days of week for full refresh schedule
const DAYS_OF_WEEK = [
  { value: 'sun', labelAr: 'الأحد', labelEn: 'Sunday' },
  { value: 'mon', labelAr: 'الاثنين', labelEn: 'Monday' },
  { value: 'tue', labelAr: 'الثلاثاء', labelEn: 'Tuesday' },
  { value: 'wed', labelAr: 'الأربعاء', labelEn: 'Wednesday' },
  { value: 'thu', labelAr: 'الخميس', labelEn: 'Thursday' },
  { value: 'fri', labelAr: 'الجمعة', labelEn: 'Friday' },
  { value: 'sat', labelAr: 'السبت', labelEn: 'Saturday' },
];

// Hours for full refresh schedule (off-peak hours recommended)
const SCHEDULE_HOURS = [0, 1, 2, 3, 4, 5, 6];

const CustomInputRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 12px;

  input {
    flex: 1;
    padding: 10px 14px;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #3b82f6;
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  button {
    padding: 10px 20px;
    border: none;
    border-radius: 8px;
    background: #3b82f6;
    color: white;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.2s;

    &:hover:not(:disabled) {
      background: #2563eb;
    }

    &:disabled {
      background: #94a3b8;
      cursor: not-allowed;
    }
  }
`;

const StatusText = styled.div`
  margin-top: 12px;
  padding: 10px 14px;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  font-size: 13px;
  color: #15803d;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    flex-shrink: 0;
  }
`;

const ScheduleRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-top: 12px;
`;

const SelectWrapper = styled.div`
  flex: 1;

  label {
    display: block;
    font-size: 12px;
    color: #64748b;
    margin-bottom: 6px;
    font-weight: 500;
  }

  select {
    width: 100%;
    padding: 10px 14px;
    border: 2px solid #e2e8f0;
    border-radius: 8px;
    font-size: 14px;
    background: white;
    cursor: pointer;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: #3b82f6;
    }

    &:disabled {
      background: #f1f5f9;
      cursor: not-allowed;
    }
  }
`;

const ToggleSwitch = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: 2px solid ${props => props.active ? '#10b981' : '#e2e8f0'};
  border-radius: 8px;
  background: ${props => props.active ? '#ecfdf5' : 'white'};
  color: ${props => props.active ? '#059669' : '#64748b'};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;
  justify-content: center;

  &:hover {
    border-color: ${props => props.active ? '#10b981' : '#3b82f6'};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

function DashboardSettings({ isOpen, onClose }) {
  const {
    topN, language, setTopN, setLanguage, resetSettings, isRTL,
    refreshInterval, refreshLoading, setRefreshInterval
  } = useSettings();
  const { t } = useTranslation();

  // State for custom refresh interval input
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customMinutes, setCustomMinutes] = useState('');

  // State for full refresh schedule
  const [fullRefreshSchedule, setFullRefreshSchedule] = useState({
    day: 'sun',
    hour: 3,
    enabled: true
  });
  const [scheduleLoading, setScheduleLoading] = useState(false);

  // Fetch full refresh schedule on mount
  const fetchFullRefreshSchedule = useCallback(async () => {
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();
      const response = await fetch(`${apiUrl}/dashboard/full-refresh-schedule`);
      if (response.ok) {
        const data = await response.json();
        setFullRefreshSchedule({
          day: data.day || 'sun',
          hour: data.hour || 3,
          enabled: data.enabled !== false
        });
      }
    } catch (err) {
      console.error('Failed to fetch full refresh schedule:', err);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchFullRefreshSchedule();
    }
  }, [isOpen, fetchFullRefreshSchedule]);

  const handleReset = () => {
    resetSettings();
  };

  // Check if current interval matches any preset
  const isPresetActive = (presetValue) => {
    return refreshInterval === presetValue && !showCustomInput;
  };

  // Check if current interval is a custom value (not matching any preset)
  const isCustomActive = () => {
    if (showCustomInput) return true;
    return refreshInterval > 0 && !REFRESH_PRESETS.some(p => p.value === refreshInterval);
  };

  // Handle preset selection
  const handlePresetClick = async (value) => {
    setShowCustomInput(false);
    setCustomMinutes('');
    await setRefreshInterval(value);
  };

  // Handle custom value save
  const handleCustomSave = async () => {
    const mins = parseInt(customMinutes, 10);
    if (mins >= MIN_INTERVAL_MINUTES) {
      const result = await setRefreshInterval(mins);
      if (result.success) {
        setShowCustomInput(false);
        setCustomMinutes('');
      }
    }
  };

  // Handle full refresh schedule change
  const handleScheduleChange = async (field, value) => {
    setScheduleLoading(true);
    try {
      await appConfig.load();
      const apiUrl = appConfig.getMainApiUrl();

      const params = new URLSearchParams();
      if (field === 'day') params.append('day', value);
      if (field === 'hour') params.append('hour', value);
      if (field === 'enabled') params.append('enabled', value);

      const response = await fetch(`${apiUrl}/dashboard/full-refresh-schedule?${params}`, {
        method: 'POST'
      });

      if (response.ok) {
        const data = await response.json();
        setFullRefreshSchedule({
          day: data.day || 'sun',
          hour: data.hour || 3,
          enabled: data.enabled !== false
        });
      }
    } catch (err) {
      console.error('Failed to update full refresh schedule:', err);
    } finally {
      setScheduleLoading(false);
    }
  };

  // Format interval for display
  const formatInterval = (minutes) => {
    if (minutes === 0) return language === 'ar' ? 'إيقاف' : 'Off';
    if (minutes < 60) return language === 'ar' ? `${minutes} دقيقة` : `${minutes} minutes`;
    if (minutes === 60) return language === 'ar' ? '1 ساعة' : '1 hour';
    if (minutes % 60 === 0) {
      const hours = minutes / 60;
      return language === 'ar' ? `${hours} ساعات` : `${hours} hours`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return language === 'ar' ? `${hours} ساعة و ${mins} دقيقة` : `${hours}h ${mins}m`;
  };

  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} />
      <Drawer isOpen={isOpen} isRTL={isRTL}>
        <Header>
          <Title>
            <SettingsIcon size={20} />
            {t('settings.title')}
          </Title>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </Header>

        <Content>
          <Section>
            <SectionTitle>{t('settings.itemsPerChart')}</SectionTitle>
            <OptionGroup>
              {TOP_N_OPTIONS.map(n => (
                <OptionButton
                  key={n}
                  active={topN === n}
                  onClick={() => setTopN(n)}
                >
                  {n}
                </OptionButton>
              ))}
            </OptionGroup>
          </Section>

          <Section>
            <SectionTitle>{t('settings.language')}</SectionTitle>
            <LanguageToggle>
              <LanguageOption
                active={language === 'ar'}
                onClick={() => setLanguage('ar')}
              >
                {t('settings.arabic')} (AR)
              </LanguageOption>
              <LanguageOption
                active={language === 'en'}
                onClick={() => setLanguage('en')}
              >
                {t('settings.english')} (EN)
              </LanguageOption>
            </LanguageToggle>
          </Section>

          <Section>
            <SectionTitle>
              <RefreshCw size={14} />
              {language === 'ar' ? 'التحديث التلقائي السريع' : 'Quick Auto-Refresh'}
            </SectionTitle>
            <SectionDescription>
              {language === 'ar'
                ? 'تحديث سريع (~5 ثواني) يعمل على فترات منتظمة'
                : 'Fast incremental refresh (~5 seconds) runs at regular intervals'}
            </SectionDescription>
            <OptionGroup>
              {REFRESH_PRESETS.map(preset => (
                <OptionButton
                  key={preset.value}
                  active={isPresetActive(preset.value)}
                  onClick={() => handlePresetClick(preset.value)}
                  disabled={refreshLoading}
                >
                  {language === 'ar' ? preset.labelAr : preset.labelEn}
                </OptionButton>
              ))}
              <OptionButton
                active={isCustomActive()}
                onClick={() => setShowCustomInput(true)}
                disabled={refreshLoading}
              >
                {language === 'ar' ? 'مخصص' : 'Custom'}
              </OptionButton>
            </OptionGroup>

            {showCustomInput && (
              <CustomInputRow>
                <input
                  type="number"
                  min={MIN_INTERVAL_MINUTES}
                  value={customMinutes}
                  onChange={(e) => setCustomMinutes(e.target.value)}
                  placeholder={language === 'ar' ? 'دقائق (حد أدنى 60)' : 'Minutes (min 60)'}
                  disabled={refreshLoading}
                />
                <button
                  onClick={handleCustomSave}
                  disabled={!customMinutes || parseInt(customMinutes, 10) < MIN_INTERVAL_MINUTES || refreshLoading}
                >
                  {refreshLoading
                    ? (language === 'ar' ? 'جاري...' : 'Saving...')
                    : (language === 'ar' ? 'حفظ' : 'Save')
                  }
                </button>
              </CustomInputRow>
            )}

            {refreshInterval > 0 && (
              <StatusText>
                <Clock size={14} />
                {language === 'ar'
                  ? `سيتم التحديث السريع كل ${formatInterval(refreshInterval)}`
                  : `Quick refresh every ${formatInterval(refreshInterval)}`
                }
              </StatusText>
            )}
          </Section>

          <Section>
            <SectionTitle>
              <Calendar size={14} />
              {language === 'ar' ? 'جدول التحديث الكامل' : 'Full Refresh Schedule'}
            </SectionTitle>
            <SectionDescription>
              {language === 'ar'
                ? 'تحديث كامل (~15 دقيقة) يعمل مرة أسبوعياً لضمان تناسق البيانات'
                : 'Full refresh (~15 minutes) runs weekly to ensure data consistency'}
            </SectionDescription>

            <ToggleSwitch
              active={fullRefreshSchedule.enabled}
              onClick={() => handleScheduleChange('enabled', !fullRefreshSchedule.enabled)}
              disabled={scheduleLoading}
            >
              {fullRefreshSchedule.enabled
                ? (language === 'ar' ? 'مفعّل' : 'Enabled')
                : (language === 'ar' ? 'معطّل' : 'Disabled')
              }
            </ToggleSwitch>

            {fullRefreshSchedule.enabled && (
              <ScheduleRow>
                <SelectWrapper>
                  <label>{language === 'ar' ? 'اليوم' : 'Day'}</label>
                  <select
                    value={fullRefreshSchedule.day}
                    onChange={(e) => handleScheduleChange('day', e.target.value)}
                    disabled={scheduleLoading}
                  >
                    {DAYS_OF_WEEK.map(day => (
                      <option key={day.value} value={day.value}>
                        {language === 'ar' ? day.labelAr : day.labelEn}
                      </option>
                    ))}
                  </select>
                </SelectWrapper>

                <SelectWrapper>
                  <label>{language === 'ar' ? 'الساعة (توقيت السعودية)' : 'Hour (KSA Time)'}</label>
                  <select
                    value={fullRefreshSchedule.hour}
                    onChange={(e) => handleScheduleChange('hour', parseInt(e.target.value, 10))}
                    disabled={scheduleLoading}
                  >
                    {SCHEDULE_HOURS.map(hour => (
                      <option key={hour} value={hour}>
                        {`${hour.toString().padStart(2, '0')}:00`}
                      </option>
                    ))}
                  </select>
                </SelectWrapper>
              </ScheduleRow>
            )}

            {fullRefreshSchedule.enabled && (
              <StatusText>
                <Calendar size={14} />
                {language === 'ar'
                  ? `التحديث الكامل: ${DAYS_OF_WEEK.find(d => d.value === fullRefreshSchedule.day)?.labelAr} الساعة ${fullRefreshSchedule.hour.toString().padStart(2, '0')}:00 (توقيت السعودية)`
                  : `Full refresh: ${DAYS_OF_WEEK.find(d => d.value === fullRefreshSchedule.day)?.labelEn} at ${fullRefreshSchedule.hour.toString().padStart(2, '0')}:00 KSA`
                }
              </StatusText>
            )}
          </Section>
        </Content>

        <Footer>
          <ResetButton onClick={handleReset}>
            <RotateCcw size={16} />
            {t('settings.reset')}
          </ResetButton>
        </Footer>
      </Drawer>
    </>
  );
}

export default DashboardSettings;
